import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { Dictionary, isFinite, startCase } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CostRate } from 'api/cost-rates';
import { UserPermissionForProject } from 'api/permissions';
import {
  AddProjectUserLiteProps,
  AddTeamToProjectPayload,
  AddTeamToProjectResponse,
  BulkShiftDatesRequest,
  CreateProjectPayload,
  Project,
  ProjectAPI,
  ProjectBulkMergePayload,
  ProjectBulkMergeResult,
  ProjectFieldValue,
  ProjectUserIdentifier,
  ProjectUserLite,
  ProjectUserLiteMembership,
  ProjectUserLiteMutationProps,
  ProjectUsersLiteQueryKey,
  RemoveProjectUserLiteProps,
  RemoveTeamFromProjectPayload,
  RemoveTeamFromProjectResponse,
  UpdateProjectPayload,
  ProjectFinancialsApiData,
  ProjectFinancialsTrackerApiData,
  ProjectFinancialsDetails,
  EventSource,
  ShiftTasksPayload,
  DependenciesMap,
  ProjectFinancialsApiQueryParams,
  ProjectFinancialsBudgets,
  ProjectBudgetMutationPayload,
  BudgetMemberExclusionItem,
  ProjectBudgetItemLite,
  ProjectBudgetDeletePayload,
} from 'api/project';
import { FieldValueType } from 'api/tasks';
import { toastService } from 'components/molecules/toast';
import { projectCreateStatusQueryKey, useProjectDetail } from 'data/projects';
import { saveTaskDetailSaga } from 'data/tasks/actions';
import { useTranslate } from 'hooks/use-translation';
import { getProjectMembersQueryKey } from 'queries';
import { logger } from 'services';
import { addProjectToTeams } from 'utils/idb-utils';
import { queueMacrotask } from 'utils/macrotask';
import { DateType, ProjectId } from '../api/models';
import { ACCOUNT_META_DATA_QUERY_KEY } from './account';
import {
  getProjectDependencyMapKey,
  getProjectDetailQueryKey,
  getProjectTasksQueryKey,
  getProjectUsersLiteKey,
} from './common';
import {
  tenMinutes,
  ALL_PROJECTS_QUERY_KEY,
  ARCHIVED_PROJECTS_QUERY_KEY,
  PROJECTS_META_QUERY_KEY,
} from './constants';
import { ApiErrorData } from './error';
import { MEMBERS_V2_QUERY_KEY, MEMBERS_V2_MODIFIED_QUERY_KEY } from './members';
import { useHideNonStrategicPartner } from './partner';
import { PlaceholderResolutionsQueryKey } from './placeholder';

export const getFinancialBudgetsLiteQueryKey = (
  projectIds: number[],
): FinancialBudgetsLiteQueryKey => ['financial-budgets-lite', projectIds];

const useProjectDetailQuery = (
  projectId: ProjectId,
  options: UseQueryOptions<Project, ApiErrorData, Project, ProjectId> = {},
): UseQueryResult<Project, ApiErrorData> => {
  const { isCurrentUserCustomer, isNonStrategicCompany } =
    useHideNonStrategicPartner();

  const isEnabled =
    options?.enabled ??
    Boolean(projectId && projectId[1] !== null && projectId[1] !== -1);

  return useQuery(
    getProjectDetailQueryKey(projectId || ['project_id', 0]),
    () => ProjectAPI.getProjectDetails(projectId[1]),
    {
      staleTime: tenMinutes,
      select: (project) => ({
        ...project,
        partners: isCurrentUserCustomer()
          ? project.partners.filter((x) => !isNonStrategicCompany(x))
          : project.partners,
      }),
      ...options,
      refetchOnWindowFocus: false,
      enabled: isEnabled,
    },
  );
};

const useRefetchProjectDetail = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (projectId: number) =>
      queryClient.invalidateQueries(
        getProjectDetailQueryKey(ProjectId(projectId)),
      ),
    [queryClient],
  );
};

const useCreateProjectMutation = (
  options: UseMutationOptions<Project, unknown, CreateProjectPayload> = {},
): UseMutationResult<Project, unknown, CreateProjectPayload> => {
  const queryClient = useQueryClient();

  return useMutation<Project, unknown, CreateProjectPayload>(
    (payload) => ProjectAPI.createProject(payload),
    {
      onSuccess: async () => {
        // Todo @Muskaan - Remove below 2 queries once pagination goes out
        await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
        await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        await queryClient.invalidateQueries(MEMBERS_V2_QUERY_KEY);
        await queryClient.refetchQueries(MEMBERS_V2_MODIFIED_QUERY_KEY);
      },
      ...options,
    },
  );
};

const useUpdateProjectMutation = (
  [, projectId]: ProjectId,
  options: UseMutationOptions<Project, unknown, UpdateProjectPayload> = {},
): UseMutationResult<Project, unknown, UpdateProjectPayload> => {
  const queryClient = useQueryClient();

  return useMutation<Project, unknown, UpdateProjectPayload>(
    (payload) => ProjectAPI.updateProject(projectId, payload),
    {
      onSuccess: async (response) => {
        await queryClient.setQueryData(
          getProjectDetailQueryKey(ProjectId(projectId)),
          response,
        );
        // Todo @Muskaan - Remove below 2 queries once pagination goes out
        await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
        await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
      },
      ...options,
    },
  );
};

type ExportProjectAuditLogPayload = Pick<Project, 'projectId'>;

const useExportProjectAuditLogMutation = (): UseMutationResult<
  { downloadUrl: string },
  unknown,
  ExportProjectAuditLogPayload
> => {
  return useMutation<
    { downloadUrl: string },
    unknown,
    ExportProjectAuditLogPayload
  >(['export_project_audit_logs'], ({ projectId }) =>
    ProjectAPI.exportProjectAuditLogs(projectId),
  );
};

type DeleteProjectPayload = Pick<Project, 'projectId'>;

const useDeleteProjectMutation = (
  onSuccess?: () => void,
): UseMutationResult<void, unknown, DeleteProjectPayload> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, DeleteProjectPayload>(
    ({ projectId }) => ProjectAPI.deleteProject(projectId),
    {
      onSuccess: async () => {
        // Todo @Muskaan - Remove below 2 queries once pagination goes out
        await queryClient.refetchQueries(ALL_PROJECTS_QUERY_KEY);
        await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        if (onSuccess) {
          onSuccess();
        }
      },
    },
  );
};

type ArchiveProjectPayload = Pick<Project, 'projectId'>;

const useArchiveProjectMutation = (
  onSuccess?: () => void,
): UseMutationResult<void, unknown, ArchiveProjectPayload> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, ArchiveProjectPayload>(
    ({ projectId }) => ProjectAPI.archiveProject(projectId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(ARCHIVED_PROJECTS_QUERY_KEY);
        // Todo @Muskaan - Remove below 2 queries once pagination goes out
        await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
        await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        if (onSuccess) {
          onSuccess();
        }
      },
    },
  );
};
const useUnarchiveProjectMutation = (): UseMutationResult<
  void,
  unknown,
  ArchiveProjectPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, ArchiveProjectPayload>(
    ({ projectId }) => ProjectAPI.unarchiveProject(projectId),
    {
      onSuccess: () => {
        // Queue to next tick so that onSuccess will work fine without causing a re-render
        queueMacrotask(async () => {
          await queryClient.invalidateQueries(ARCHIVED_PROJECTS_QUERY_KEY);
          // Todo @Muskaan - Remove below 2 queries once pagination goes out
          await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
          await queryClient.invalidateQueries(PROJECTS_META_QUERY_KEY);
          await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        });
      },
    },
  );
};

const useProjectBulkMergeMutation = (
  projectId: ProjectId,
): UseMutationResult<Project, unknown, ProjectBulkMergePayload[]> => {
  const queryClient = useQueryClient();

  return useMutation<Project, unknown, ProjectBulkMergePayload[]>(
    (payload) => ProjectAPI.bulkMerge(projectId[1], payload),
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(
          projectCreateStatusQueryKey(projectId[1]),
        );
        await queryClient.invalidateQueries(
          getProjectDetailQueryKey(projectId),
        );
        await queryClient.invalidateQueries(getProjectTasksQueryKey(projectId));
      },
    },
  );
};

const useProjectBulkMergePreviewMutation = ([
  ,
  projectId,
]: ProjectId): UseMutationResult<
  ProjectBulkMergeResult[],
  unknown,
  ProjectBulkMergePayload[]
> => {
  return useMutation<
    ProjectBulkMergeResult[],
    unknown,
    ProjectBulkMergePayload[]
  >((payload) => ProjectAPI.bulkMergePreview(projectId, payload));
};

export type ProjectGridPayload = {
  projectId: number;
  payload: UpdateProjectPayload;
  /** Pre-edit cell value so ag-grid can revert if the request fails (cache/facts may be missing or stale). */
  gridRevert?: {
    fieldName: string;
    previousValue: unknown;
  };
};

export type ProjectGridMutationErrorContext = {
  project?: Project;
  meta?: Project;
  mutationInput: ProjectGridPayload;
  error: unknown;
};

const getAllProjectsQueryUpdater = (newProjects: Project[]) => {
  const updater = (oldData: Project[] | undefined): any => {
    logger.log('trying to update based on edit success status', newProjects);
    if (!oldData) {
      return oldData;
    }

    return oldData.map((project) => {
      const predicateByProjectId = ({ projectId }) =>
        project.projectId === projectId;
      const updatedProject = newProjects.find(predicateByProjectId) || project;

      return updatedProject;
    });
  };

  return updater;
};

type UpdateProject = (newProject: Project) => void;

const useToUpdateProjectsQuery = (queryKey: string[]): UpdateProject => {
  const queryClient = useQueryClient();

  const [projectById, setProjectById] = useState<Record<string, Project>>({});
  const updateQueryClient = useRef<(x0: Project[]) => void>();

  useEffect(() => {
    const updateProject = (newProjects: Project[]) => {
      queryClient.setQueryData(
        queryKey,
        getAllProjectsQueryUpdater(newProjects),
      );
      setProjectById({});
    };

    updateQueryClient.current = updateProject;
  }, [queryClient, queryKey]);

  const updateProjectToQueryClient = (newProject: Project) => {
    const updatedProjectById = {
      ...projectById,
      [newProject.projectId]: newProject,
    };

    setProjectById(updatedProjectById);
    updateQueryClient.current?.(Object.values(updatedProjectById));
  };

  return updateProjectToQueryClient;
};

const patchNativeProjectFields = (
  project: Project,
  payload: UpdateProjectPayload,
) => {
  const [keyChanged] = Object.keys(payload);
  const [value] = Object.values(payload);

  return {
    ...project,
    [keyChanged]: value,
  };
};

const patchProjectByPayload = (
  project: Project,
  payload: UpdateProjectPayload,
): Project => {
  if (!payload) {
    return project;
  }

  if (!payload.fields) {
    return patchNativeProjectFields(project, payload);
  }
  const newProject: Project = JSON.parse(JSON.stringify(project));
  const [payloadField] = payload.fields as ProjectFieldValue<FieldValueType>[];

  const predicateByFieldId = (field: ProjectFieldValue<FieldValueType>) =>
    field.fieldId === payloadField.fieldId;
  const existingProjectField = newProject.fields.find(predicateByFieldId);

  if (!existingProjectField) {
    newProject.fields.push(payloadField);

    return newProject;
  }
  existingProjectField.fieldValue = payloadField.fieldValue;

  return newProject;
};

const useProjectGridToUpdate = ({
  onError,
}: {
  onError: (ctx: ProjectGridMutationErrorContext) => void;
}): UseMutationResult<
  Project,
  unknown,
  ProjectGridPayload,
  {
    fallback: {
      project: Project | undefined;
      meta: Project | undefined;
    };
  }
> => {
  const queryClient = useQueryClient();
  const updateProjectQuery = useToUpdateProjectsQuery(ALL_PROJECTS_QUERY_KEY);
  const updateProjectMeta = useToUpdateProjectsQuery(PROJECTS_META_QUERY_KEY);
  const translate = useTranslate();

  return useMutation(
    ({ projectId, payload }: ProjectGridPayload) => {
      return ProjectAPI.updateProject(projectId, payload);
    },
    {
      onSuccess: (project: Project) => {
        const { projectId } = project;

        updateProjectQuery(project);
        queryClient.invalidateQueries(
          getProjectDetailQueryKey(ProjectId(projectId)),
        );
      },
      onMutate: async ({ projectId, payload }: ProjectGridPayload) => {
        const projectQueryKey = getProjectDetailQueryKey(ProjectId(projectId));

        const predicateById = (project: Project) =>
          project.projectId === projectId;

        const previous =
          queryClient.getQueryData<Project>(projectQueryKey) ||
          queryClient
            .getQueryData<Project[]>(ALL_PROJECTS_QUERY_KEY)
            ?.find(predicateById);
        const previousMeta = queryClient
          .getQueryData<Project[]>(PROJECTS_META_QUERY_KEY)
          ?.find(predicateById);

        if (previous) {
          const newProject = patchProjectByPayload(previous, payload);

          updateProjectQuery(newProject);
        }
        if (previousMeta && !payload.fields) {
          const newMeta = patchNativeProjectFields(previousMeta, payload);

          updateProjectMeta(newMeta);
        }

        return {
          fallback: {
            project: previous,
            meta: previousMeta,
          },
        };
      },
      onError: (err, mutationInput, context) => {
        const { fallback } = context || {};

        if (fallback?.project) {
          updateProjectQuery(fallback.project);
        }
        if (fallback?.meta) {
          updateProjectMeta(fallback.meta);
        }
        onError({
          meta: fallback?.meta,
          project: fallback?.project,
          mutationInput,
          error: err,
        });
        const fieldName = startCase(Object.keys(mutationInput.payload)?.[0]);
        const projectName = fallback?.project?.projectName;

        const errorTitle = translate('project.error.unauth.update.title');
        const errorMsg = translate('project.error.unauth.update.message', {
          fieldName,
          projectName: projectName ?? '',
          bold: (chunks) => <strong>{chunks}</strong>,
        });

        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        if (err instanceof Error && err?.request?.status === 403) {
          toastService({
            title: errorTitle,
            message: errorMsg,
          }).error();
        }
      },
      retry: false,
    },
  );
};

const addTeamsAPI = (
  projectId: number,
  teams: AddTeamToProjectPayload[],
): Promise<AddTeamToProjectResponse[]> => ProjectAPI.addTeams(projectId, teams);

const useAddTeamsToProjectMutation = (
  options: UseMutationOptions<
    AddTeamToProjectResponse[],
    unknown,
    {
      projectId: number;
      teamIds: number[];
      projectFinancialsBudgetIds: number[] | undefined;
    }
  > = {},
): UseMutationResult<
  AddTeamToProjectResponse[],
  unknown,
  {
    projectId: number;
    teamIds: number[];
    projectFinancialsBudgetIds: number[] | undefined;
  }
> =>
  useMutation(
    async ({ projectId, teamIds, projectFinancialsBudgetIds }) => {
      const team = await addTeamsAPI(
        projectId,
        teamIds.map((teamId) => ({ teamId, projectFinancialsBudgetIds })),
      );

      addProjectToTeams({
        projectId,
        teamIds,
      }).catch((err) => {
        logger.error(
          `Error occured while adding projectId to team. teamIds: ${teamIds}`,
          err,
        );
      });

      return team;
    },

    options,
  );

const removeTeamsAPI = (
  projectId: number,
  teams: RemoveTeamFromProjectPayload[],
): Promise<RemoveTeamFromProjectResponse[]> =>
  ProjectAPI.removeTeams(projectId, teams);

const useRemoveTeamsFromProjectMutation = (
  options: UseMutationOptions<
    RemoveTeamFromProjectResponse[],
    unknown,
    { projectId: number; teamIds: number[] }
  > = {},
): UseMutationResult<
  RemoveTeamFromProjectResponse[],
  unknown,
  { projectId: number; teamIds: number[] }
> =>
  useMutation(
    ({ projectId, teamIds }) =>
      removeTeamsAPI(
        projectId,
        teamIds.map((teamId) => ({ teamId })),
      ),
    options,
  );

const useProjectUsersLiteQuery = (
  queryOptions: UseQueryOptions<
    Dictionary<ProjectUserLite[]>,
    unknown,
    Dictionary<ProjectUserLite[]>,
    ProjectUsersLiteQueryKey
  > = {},
): UseQueryResult<Dictionary<ProjectUserLite[]>> =>
  useQuery(
    getProjectUsersLiteKey(),
    () =>
      ProjectAPI.getAllProjectUsersLite().then((data) => {
        const lookup: Dictionary<ProjectUserLite[]> = {};

        data.forEach(({ projectId, members }) => {
          lookup[projectId] = (lookup[projectId] ?? []).concat(members);
        });

        return lookup;
      }),
    {
      cacheTime: 0,
      refetchOnWindowFocus: false, // ? This was handled globally right ?
      ...queryOptions,
    },
  );

const useProjectUsersLiteForProjectQuery = (
  projectId: number,
  queryOptions: UseQueryOptions<
    ProjectUserLiteMembership,
    unknown,
    ProjectUserLiteMembership,
    ProjectUsersLiteQueryKey
  > = {},
): UseQueryResult<ProjectUserLiteMembership> =>
  useQuery(
    getProjectUsersLiteKey(projectId),
    () => ProjectAPI.getProjectUsersLiteForProject(projectId),
    {
      refetchOnWindowFocus: false,
      ...queryOptions,
    },
  );

const filterExistingProjectUsers = (
  projectId: number,
  projectUser: ProjectUserLite,
  oldState: Dictionary<ProjectUserLite[]> | undefined = {},
) => {
  const {
    user: { userId },
    role: projectUserRole,
    referenceTeam: projectUserReferenceTeam,
  } = projectUser;

  return (
    oldState[projectId]?.filter(
      ({ user, role, referenceTeam }) =>
        !(
          user.userId === userId &&
          role?.roleId === projectUserRole?.roleId &&
          referenceTeam?.teamId === projectUserReferenceTeam?.teamId
        ),
    ) ?? []
  );
};

const useAddProjectUserLiteToQueryState = (): ((
  projectId: number,
  projectUser: ProjectUserLite,
) => void) => {
  const queryClient = useQueryClient();

  return useCallback(
    (projectId: number, projectUser: ProjectUserLite) => {
      queryClient.setQueryData<Dictionary<ProjectUserLite[]>>(
        getProjectUsersLiteKey(),
        (state = {}) => ({
          ...state,
          [projectId]: filterExistingProjectUsers(
            projectId,
            projectUser,
            state,
          ).concat(projectUser),
        }),
      );
    },
    [queryClient],
  );
};

const useRemoveProjectUserLiteFromQueryState = (): ((
  projectId: number,
  projectUser: ProjectUserLite,
) => void) => {
  const queryClient = useQueryClient();

  return useCallback(
    (projectId: number, projectUser: ProjectUserLite) => {
      queryClient.setQueryData<Dictionary<ProjectUserLite[]>>(
        getProjectUsersLiteKey(),
        (state = {}) => ({
          ...state,
          [projectId]: filterExistingProjectUsers(
            projectId,
            projectUser,
            state,
          ),
        }),
      );
    },
    [queryClient],
  );
};

const useAddProjectUsersLiteMutation = (
  options: UseMutationOptions<
    AddProjectUserLiteProps,
    unknown,
    {
      projectId: number;
      projectUsers: ProjectUserIdentifier[];
      source?: EventSource;
      assignedToProjectMember?: boolean;
    }
  > = {},
): UseMutationResult<
  AddProjectUserLiteProps,
  unknown,
  {
    projectId: number;
    projectUsers: ProjectUserIdentifier[];
    source?: EventSource;
    assignedToProjectMember?: boolean;
  }
> =>
  useMutation(
    ({
      projectId,
      projectUsers,
      source,
      assignedToProjectMember,
    }: ProjectUserLiteMutationProps) =>
      ProjectAPI.addProjectUsersLite(projectId, {
        members: projectUsers,
        source,
        assignedToProjectMember,
      }),
    options,
  );

const useRemoveProjectUsersLiteMutation = (
  options: UseMutationOptions<
    RemoveProjectUserLiteProps,
    unknown,
    {
      projectId: number;
      projectUsers: ProjectUserIdentifier[];
      source?: EventSource;
      assignedToProjectMember?: boolean;
    }
  > = {},
): UseMutationResult<
  RemoveProjectUserLiteProps,
  unknown,
  {
    projectId: number;
    projectUsers: ProjectUserIdentifier[];
    source?: EventSource;
    assignedToProjectMember?: boolean;
  }
> =>
  useMutation(
    ({
      projectId,
      projectUsers: usersReplacingTeams,
      source,
    }: ProjectUserLiteMutationProps) =>
      ProjectAPI.removeProjectUsersLite(projectId, {
        members: usersReplacingTeams,
        source,
      }),
    options,
  );
const useAddPartnersToProjectMutation = (
  options: UseMutationOptions<
    unknown,
    unknown,
    { projectId: number; partners: number[] }
  > = {},
): UseMutationResult<
  unknown,
  unknown,
  { projectId: number; partners: number[] }
> => {
  const queryClient = useQueryClient();
  const translate = useTranslate();

  return useMutation(
    ({ projectId, partners }) => ProjectAPI.addPartners(projectId, partners),
    {
      onSuccess: (_, params) => {
        queryClient.invalidateQueries(
          getProjectDetailQueryKey(ProjectId(params.projectId)),
        );
        toastService({
          title: translate('project.invitation.add_partners.success.title', {
            count: params.partners.length,
          }),
          message: '',
        }).success();
      },
      onError: (_, params) => {
        toastService({
          title: translate('project.invitation.add_partners.error.title', {
            count: params.partners.length,
          }),
          message: '',
        }).error();
      },
      ...options,
    },
  );
};
const useRemovePartnersToProjectMutation = (
  options: UseMutationOptions<
    unknown,
    unknown,
    { projectId: number; partners: number[] }
  > = {},
): UseMutationResult<
  unknown,
  unknown,
  { projectId: number; partners: number[] }
> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ projectId, partners }) => ProjectAPI.removePartners(projectId, partners),
    {
      onSuccess: (_, params) => {
        queryClient.invalidateQueries(
          getProjectDetailQueryKey(ProjectId(params.projectId)),
        );
      },
      ...options,
    },
  );
};

export const useBulkShiftDatesMutation = (
  options: UseMutationOptions<
    void,
    unknown,
    {
      projectId: number;
      payload: BulkShiftDatesRequest;
    }
  > = {},
) =>
  useMutation(
    ({
      projectId,
      payload,
    }: {
      projectId: number;
      payload: BulkShiftDatesRequest;
    }) => ProjectAPI.bulkShiftDates(projectId, payload),
    options,
  );

export type ProjectFinancialsQueryKey = [
  'project-financials',
  number,
  number,
  boolean,
];

export const getProjectFinancialsQueryKey = (
  projectId: number,
  budgetId: number = -1,
  includeSubscriptionMeta = false,
): ProjectFinancialsQueryKey => [
  'project-financials',
  projectId,
  budgetId,
  includeSubscriptionMeta,
];

export const useProjectFinancialsQuery = (
  projectId: number,
  includeSubscriptionMeta?: boolean,
  subscriptionPeriodId?: DateType,
  budgetId?: number,
  queryParams?: ProjectFinancialsApiQueryParams,
  queryOptions: UseQueryOptions<
    ProjectFinancialsApiData,
    unknown,
    ProjectFinancialsApiData,
    ProjectFinancialsQueryKey
  > = {},
): UseQueryResult<ProjectFinancialsApiData> =>
  useQuery(
    getProjectFinancialsQueryKey(projectId, budgetId, includeSubscriptionMeta),
    () =>
      ProjectAPI.getProjectFinancials({
        projectId,
        includeSubscriptionMeta,
        subscriptionPeriodId,
        queryParams,
        budgetId,
      }),
    {
      ...queryOptions,
      cacheTime: 0,
    },
  );

export type ProjectFinancialsTrackerQueryKey = [
  'project-financials-tracker',
  number,
];

export const getProjectFinancialsTrackerQueryKey = (
  projectId: number,
): ProjectFinancialsTrackerQueryKey => [
  'project-financials-tracker',
  projectId,
];

export const useProjectFinancialsTrackerQuery = (
  projectId: number,
  queryOptions: UseQueryOptions<
    ProjectFinancialsTrackerApiData,
    unknown,
    ProjectFinancialsTrackerApiData,
    ProjectFinancialsTrackerQueryKey
  > = {},
): UseQueryResult<ProjectFinancialsTrackerApiData> =>
  useQuery(
    getProjectFinancialsTrackerQueryKey(projectId),
    () =>
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      ProjectAPI.getProjectFinancialsData(
        `/projects/${projectId}/financials/trackers`,
      ),
    {
      ...queryOptions,
      cacheTime: 0,
    },
  );

export type ProjectPermissionsQueryKey = ['project-permissions', number];

export const getProjectPermissionsQueryKey = (
  projectId: number,
): ProjectPermissionsQueryKey => ['project-permissions', projectId];

export const useProjectPermissionsQuery = (
  projectId: number,
  queryOptions: UseQueryOptions<
    UserPermissionForProject,
    unknown,
    UserPermissionForProject,
    ProjectPermissionsQueryKey
  > = {},
): UseQueryResult<UserPermissionForProject> =>
  useQuery(
    getProjectPermissionsQueryKey(projectId),
    () => ProjectAPI.getProjectPermissionsForCurrentUser(projectId),
    {
      ...queryOptions,
    },
  );

export const RateCardsProjectSettingsQueryKey = (
  projectId: number,
): [string, number] => ['rate-cards-project-settings', projectId];

export const RateCardsProjectSettingsQueryKeyV2 = (
  projectId: number,
): [string, number] => ['rate-cards-project-settings-v2', projectId];

/**
 * @deprecated use the new V2 query after multi line budgets
 */
export const useProjectsBudgetAndRateSettingsQuery = (
  projectId: number,
  queryOptions: UseQueryOptions<
    ProjectFinancialsDetails,
    unknown,
    ProjectFinancialsDetails,
    [string, number]
  > = {},
): UseQueryResult<ProjectFinancialsDetails> =>
  useQuery(
    RateCardsProjectSettingsQueryKey(projectId),
    () => ProjectAPI.getProjectBudgetSettings(projectId),
    {
      ...queryOptions,
      cacheTime: tenMinutes,
    },
  );

export const useProjectsBudgetAndRateSettingsV2Query = (
  projectId: number,
  queryOptions: UseQueryOptions<
    ProjectFinancialsDetails,
    unknown,
    ProjectFinancialsDetails,
    [string, number]
  > = {},
): UseQueryResult<ProjectFinancialsDetails> =>
  useQuery(
    RateCardsProjectSettingsQueryKeyV2(projectId),
    () => ProjectAPI.getProjectBudgetSettingsV2(projectId),
    {
      ...queryOptions,
      enabled:
        !!projectId && projectId !== -1 && (queryOptions.enabled ?? true),
    },
  );

export const ProjectUserTeamsQueryKey = (projectId: number) => [
  'project-user-teams',
  projectId,
];

export const useProjectUserTeamsQuery = (
  projectId: number,
  queryOptions: UseQueryOptions<
    ProjectUserLiteMembership,
    unknown,
    ProjectUserLiteMembership,
    (string | number)[]
  > = {},
): UseQueryResult<ProjectUserLiteMembership> =>
  useQuery(
    ProjectUserTeamsQueryKey(projectId),
    () => ProjectAPI.getProjectUsersLiteForProject(projectId),
    {
      ...queryOptions,
    },
  );

export const ProjectUserTeamsCostRates = (projectId: number) => [
  'project-user-team-cost-rates',
  projectId,
];

export const useProjectUserTeamsCostRatesQuery = (
  projectId: number,
  queryOptions: UseQueryOptions<
    CostRate[],
    unknown,
    CostRate[],
    (string | number)[]
  > = {},
): UseQueryResult<CostRate[]> =>
  useQuery(
    ProjectUserTeamsCostRates(projectId),
    () => ProjectAPI.getProjectUserTeamsCostRates(projectId),
    {
      ...queryOptions,
      cacheTime: 0,
    },
  );

/**
 * @deprecated use the new V2 query after multi line budgets
 */
export const useProjectsBudgetAndRateSettingsMutation = (
  projectId: number,
  options: UseMutationOptions<
    ProjectFinancialsDetails,
    unknown,
    ProjectFinancialsDetails
  > = {},
): UseMutationResult<
  ProjectFinancialsDetails,
  unknown,
  ProjectFinancialsDetails
> =>
  useMutation(
    (payload: ProjectFinancialsDetails) =>
      ProjectAPI.updateProjectBudgetSettings(projectId, payload),
    {
      ...options,
    },
  );

export const useUpdateProjectGeneralSettingsV2Mutation = (
  projectId: number,
  options: UseMutationOptions<
    ProjectFinancialsDetails,
    unknown,
    ProjectFinancialsDetails
  > = {},
): UseMutationResult<
  ProjectFinancialsDetails,
  unknown,
  ProjectFinancialsDetails
> =>
  useMutation(
    (payload) => ProjectAPI.updateProjectGeneralSettingsV2(projectId, payload),
    {
      ...options,
    },
  );

const useCreateProjectBudgetMutationV2 = (
  projectId: number,
  options: UseMutationOptions<
    ProjectFinancialsBudgets,
    unknown,
    ProjectFinancialsBudgets
  > = {},
): UseMutationResult<
  ProjectFinancialsBudgets,
  unknown,
  ProjectFinancialsBudgets
> => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload) => ProjectAPI.createProjectBudgetSettingsV2(projectId, payload),
    {
      ...options,
      onSuccess: (...props) => {
        // Invalidate budget exclusions queries for the project
        queryClient.invalidateQueries([
          'project-members-exclusion',
          ProjectId(projectId),
        ]);
        queryClient.invalidateQueries(['budget-member-exclusion']);
        options?.onSuccess?.(...props);
      },
    },
  );
};

export const useProjectsBudgetAndRateSettingsMutationV2 = (
  projectId: number,
  options: UseMutationOptions<
    ProjectFinancialsBudgets,
    unknown,
    ProjectBudgetMutationPayload
  > = {},
): UseMutationResult<
  ProjectFinancialsBudgets,
  unknown,
  ProjectBudgetMutationPayload
> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ budgetId, payload }) =>
      ProjectAPI.updateProjectBudgetSettingsV2(projectId, budgetId, payload),
    {
      ...options,
      onSuccess: (...props) => {
        // const existingPayload = queryClient.getQueryData(
        //   RateCardsProjectSettingsQueryKeyV2(projectId),
        // ) as ProjectFinancialsDetails;
        // const newPayload = {
        //   ...existingPayload,
        //   projectFinancialsBudgets: (
        //     existingPayload.projectFinancialsBudgets ?? []
        //   ).map((d) => {
        //     if (
        //       d.projectFinancialsBudgetId === data.projectFinancialsBudgetId
        //     ) {
        //       return data;
        //     }

        //     return d;
        //   }),
        // } as ProjectFinancialsDetails;

        // queryClient.setQueryData(
        //   RateCardsProjectSettingsQueryKeyV2(projectId),
        //   newPayload,
        // );
        queryClient.invalidateQueries(
          RateCardsProjectSettingsQueryKeyV2(projectId),
        );
        queryClient.invalidateQueries(
          getFinancialBudgetsLiteQueryKey([projectId]),
        );
        // Invalidate budget exclusions queries for the project
        queryClient.invalidateQueries([
          'project-members-exclusion',
          ProjectId(projectId),
        ]);
        queryClient.invalidateQueries(['budget-member-exclusion']);
        options?.onSuccess?.(...props);
      },
    },
  );
};

export const useBudgetDeleteMutation = (
  options: UseMutationOptions<void, unknown, ProjectBudgetDeletePayload> = {},
): UseMutationResult<void, unknown, ProjectBudgetDeletePayload> => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: ProjectBudgetDeletePayload) =>
      ProjectAPI.deleteProjectBudget(payload),
    {
      ...options,
      onSuccess: (_, payload, ...props) => {
        // Invalidate budget exclusions queries for the project
        queryClient.invalidateQueries([
          'project-members-exclusion',
          ProjectId(payload.projectId),
        ]);
        queryClient.invalidateQueries(['budget-member-exclusion']);
        options?.onSuccess?.(_, payload, ...props);
      },
    },
  );
};

export const useMarkBudgetAsDefaultMutation = (
  projectId: number,
  options: UseMutationOptions<void, unknown, number> = {},
): UseMutationResult<void, unknown, number> => {
  const queryClient = useQueryClient();

  return useMutation(
    (budgetId) => ProjectAPI.markBudgetAsDefault(projectId, budgetId),
    {
      ...options,
      onSuccess: (...props) => {
        // Invalidate budget exclusions queries for the project
        queryClient.invalidateQueries([
          'project-members-exclusion',
          ProjectId(projectId),
        ]);
        queryClient.invalidateQueries(['budget-member-exclusion']);
        options?.onSuccess?.(...props);
      },
    },
  );
};

export const BudgetMemberExclusionQueryKey = (
  budgetId: number,
): [string, number] => ['budget-member-exclusion', budgetId];

export const useGetBudgetMemberExclusions = (
  projectId: number,
  budgetId: number,
  queryOptions: UseQueryOptions<
    BudgetMemberExclusionItem[],
    unknown,
    BudgetMemberExclusionItem[],
    [string, number]
  > = {},
): UseQueryResult<BudgetMemberExclusionItem[]> =>
  useQuery(
    BudgetMemberExclusionQueryKey(budgetId),
    () => ProjectAPI.getMemberExclusionsForBudget(projectId, budgetId),
    {
      ...queryOptions,
      cacheTime: 0,
    },
  );

export const ProjectBudgetExclusionsQueryKey = (
  projectId: number,
): [string, number] => ['project-budget-exclusions', projectId];

export const useGetProjectBudgetExclusions = (
  projectId: number | undefined,
  queryOptions: UseQueryOptions<
    Dictionary<BudgetMemberExclusionItem[]>,
    unknown,
    Dictionary<BudgetMemberExclusionItem[]>,
    [string, number]
  > = {},
): UseQueryResult<Dictionary<BudgetMemberExclusionItem[]>> =>
  useQuery(
    ProjectBudgetExclusionsQueryKey(projectId ?? -1),
    () => {
      return ProjectAPI.getMemberExclusionsForProject(projectId ?? -1);
    },
    {
      ...queryOptions,
      enabled: !!projectId && (queryOptions.enabled ?? true),
      staleTime: tenMinutes,
    },
  );

const useUpdateFinancialBudgetMembersMutation = (
  projectId: number,
  options: UseMutationOptions<
    void,
    unknown,
    {
      projectId: number;
      budgetId: number;
      payload: { userId?: number; roleId?: number; teamId?: number }[];
    }
  > = {},
): UseMutationResult<
  void,
  unknown,
  {
    projectId: number;
    budgetId: number;
    payload: { userId?: number; roleId?: number; teamId?: number }[];
  }
> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ projectId: projId, budgetId, payload }) =>
      ProjectAPI.updateFinancialBudgetMembers(projId, budgetId, payload),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries(
          getProjectMembersQueryKey(ProjectId(projectId)),
        );
        queryClient.invalidateQueries(PlaceholderResolutionsQueryKey);
        queryClient.invalidateQueries(
          RateCardsProjectSettingsQueryKeyV2(projectId),
        );
        // Invalidate budget exclusions queries for the project
        queryClient.invalidateQueries([
          'project-members-exclusion',
          ProjectId(projectId),
        ]);
        queryClient.invalidateQueries(['budget-member-exclusion']);
      },
    },
  );
};

const useAddMembersToBudgetMutation = (
  projectId: number,
  options: UseMutationOptions<
    void,
    unknown,
    {
      budgetIds: number[];
      users: { userId: number }[];
      teams: { teamId: number }[];
    }
  > = {},
): UseMutationResult<
  void,
  unknown,
  {
    budgetIds: number[];
    users: { userId: number }[];
    teams: { teamId: number }[];
  }
> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ budgetIds, users, teams }) =>
      ProjectAPI.addMembersToBudget({ budgetIds, users, teams }),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(
          getProjectMembersQueryKey(ProjectId(projectId)),
        );
        queryClient.invalidateQueries(PlaceholderResolutionsQueryKey);
        queryClient.invalidateQueries(
          RateCardsProjectSettingsQueryKeyV2(projectId),
        );
        // Invalidate budget exclusions queries for the project
        queryClient.invalidateQueries([
          'project-members-exclusion',
          ProjectId(projectId),
        ]);
        queryClient.invalidateQueries(['budget-member-exclusion']);
        // Invalidate budget members cache for all selected budgets
        variables.budgetIds.forEach((budgetId) => {
          queryClient.invalidateQueries([
            'budget-members',
            projectId,
            budgetId,
          ]);
        });
        // Call the original onSuccess if provided
        if (options.onSuccess) {
          options.onSuccess(data, variables, undefined);
        }
      },
    },
  );
};

const useDeleteBudgetMember = (
  options: UseMutationOptions<
    void,
    unknown,
    {
      projectId: number;
      budgetId: number;
      payload: { userId?: number; roleId?: number; teamId?: number }[];
    }
  > = {},
): UseMutationResult<
  void,
  unknown,
  {
    projectId: number;
    budgetId: number;
    payload: { userId?: number; roleId?: number; teamId?: number }[];
  }
> => {
  return useMutation(
    ({ projectId, budgetId, payload }) =>
      ProjectAPI.deleteBudgetMember(projectId, budgetId, payload),
    {
      ...options,
      onSuccess: (_, variables, ...props) => {
        options?.onSuccess?.(_, variables, ...props);
      },
    },
  );
};

export const useProjectCostRateSettingsMutation = (
  projectId: number,
  options: UseMutationOptions<any, unknown, any> = {},
): UseMutationResult<any, unknown, any> =>
  useMutation(
    ({
      updatedPayload,
      backdate,
    }: {
      updatedPayload: Partial<CostRate>[];
      backdate: string;
    }) =>
      ProjectAPI.updateProjectCostRates(projectId, backdate, updatedPayload),
    {
      ...options,
    },
  );

const useShiftTasksMutation = (
  projectId: number,
): UseMutationResult<unknown, unknown, ShiftTasksPayload, unknown> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation((payload) => ProjectAPI.shiftTasks(projectId, payload), {
    onSuccess: async (response: ShiftTasksPayload) => {
      if (response) {
        response.updatedTasks.forEach((task) =>
          dispatch(saveTaskDetailSaga(task)),
        );
      }
      await queryClient.invalidateQueries(
        getProjectDetailQueryKey(ProjectId(projectId)),
      );
      await queryClient.invalidateQueries(
        getProjectTasksQueryKey(ProjectId(projectId)),
      );

      await queryClient.invalidateQueries(
        getProjectDependencyMapKey(projectId),
      );

      return response;
    },
  });
};
const useGetDependenciesMap = (
  projectId: number,
): UseQueryResult<DependenciesMap | undefined> => {
  return useQuery(
    getProjectDependencyMapKey(projectId),
    () => ProjectAPI.getDependenciesMap(projectId),
    {
      cacheTime: 0,
      enabled: isFinite(Number(projectId)),
    },
  );
};

export type FinancialBudgetsLiteQueryKey = ['financial-budgets-lite', number[]];

export const useFinancialBudgetsLiteQuery = (
  projectIds: number[],
  queryOptions: UseQueryOptions<
    Record<string, ProjectBudgetItemLite[]>,
    unknown,
    Record<string, ProjectBudgetItemLite[]>,
    FinancialBudgetsLiteQueryKey
  > = {},
): UseQueryResult<Record<string, ProjectBudgetItemLite[]>> =>
  useQuery(
    getFinancialBudgetsLiteQueryKey(projectIds),
    () => ProjectAPI.getFinancialBudgetsLite(projectIds),
    {
      enabled: projectIds.length > 0,
      ...queryOptions,
    },
  );

const useProjectDetailsWithAllBudgetFields = (
  projectId: number,
): UseQueryResult<Project, ApiErrorData> => {
  const projectQuery = useProjectDetail(ProjectId(projectId));
  const financialsQuery = useProjectsBudgetAndRateSettingsV2Query(projectId);

  // Memoize merged data so same reference when underlying data unchanged.
  // Prevents ProjectPlan memo from failing on parent re-renders (e.g. hash change).
  // Ref cache: when projectId unchanged and we have data, return cached — React Query
  // returns new refs on refetch/focus; hash-only re-renders (task drawer) don't change data.
  // Never return cached when loading (next undefined) — avoids blank page on navigation.
  const mergedDataRef = useRef<Project | undefined>();
  const mergedData = useMemo(() => {
    const next = projectQuery.data
      ? ({
          ...projectQuery.data,
          projectFinancialsDetailModel: {
            ...(projectQuery.data.projectFinancialsDetailModel ?? {}),
            projectFinancialsBudgets:
              financialsQuery.data?.projectFinancialsBudgets,
          },
        } as Project)
      : undefined;

    mergedDataRef.current = next;

    return next;
  }, [projectQuery.data, financialsQuery.data?.projectFinancialsBudgets]);

  return {
    ...projectQuery,
    data: mergedData,
    isLoading: projectQuery.isLoading || financialsQuery.isLoading,
  } as UseQueryResult<Project, ApiErrorData>;
};

export {
  useProjectDetailQuery,
  useRefetchProjectDetail,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useExportProjectAuditLogMutation,
  useDeleteProjectMutation,
  useArchiveProjectMutation,
  useUnarchiveProjectMutation,
  useProjectBulkMergeMutation,
  useProjectBulkMergePreviewMutation,
  useProjectGridToUpdate,
  useAddTeamsToProjectMutation,
  useRemoveTeamsFromProjectMutation,
  useProjectUsersLiteQuery,
  useProjectUsersLiteForProjectQuery,
  useAddProjectUserLiteToQueryState,
  useRemoveProjectUserLiteFromQueryState,
  useAddProjectUsersLiteMutation,
  useRemoveProjectUsersLiteMutation,
  useAddPartnersToProjectMutation,
  useRemovePartnersToProjectMutation,
  useShiftTasksMutation,
  useGetDependenciesMap,
  useUpdateFinancialBudgetMembersMutation,
  useAddMembersToBudgetMutation,
  useDeleteBudgetMember,
  useCreateProjectBudgetMutationV2,
  useProjectDetailsWithAllBudgetFields,
};
